/**
 * Helpers de tradução entre a visão "plana" do editor (header/body/footer/botões)
 * e o `components[]` Meta-native do backend.
 *
 * O backend persiste `components[]` (HEADER/BODY/FOOTER/BUTTONS); o gabarito edita
 * campos planos. Aqui derivamos a visão plana para leitura/preview (PR1) e
 * montamos o array de volta no submit (PR2). 1 BODY é obrigatório; HEADER/FOOTER/
 * BUTTONS são opcionais.
 */

import type { TemplateButton, TemplateComponent } from './types';

export interface FlatTemplate {
  header: string;
  body: string;
  footer: string;
  buttons: TemplateButton[];
}

function findComponent(
  components: TemplateComponent[],
  type: TemplateComponent['type'],
): TemplateComponent | undefined {
  return components.find((c) => c.type === type);
}

/** Achata `components[]` para a visão do editor/preview. */
export function flattenComponents(
  components: TemplateComponent[] | null | undefined,
): FlatTemplate {
  const list = components ?? [];
  return {
    header: findComponent(list, 'HEADER')?.text ?? '',
    body: findComponent(list, 'BODY')?.text ?? '',
    footer: findComponent(list, 'FOOTER')?.text ?? '',
    buttons: findComponent(list, 'BUTTONS')?.buttons ?? [],
  };
}

/**
 * Monta `components[]` a partir da visão plana (submit do editor · PR2).
 * Só inclui HEADER/FOOTER/BUTTONS quando preenchidos; BODY entra sempre
 * (o backend valida que existe 1 BODY).
 */
export function buildComponents(flat: FlatTemplate): TemplateComponent[] {
  const out: TemplateComponent[] = [];
  if (flat.header.trim().length > 0) {
    out.push({ type: 'HEADER', format: 'TEXT', text: flat.header.trim() });
  }
  out.push({ type: 'BODY', text: flat.body });
  if (flat.footer.trim().length > 0) {
    out.push({ type: 'FOOTER', text: flat.footer.trim() });
  }
  if (flat.buttons.length > 0) {
    out.push({ type: 'BUTTONS', buttons: flat.buttons });
  }
  return out;
}

/**
 * Conta variáveis `{{n}}` distintas no corpo (preview/contagem · espelha o
 * gabarito, que detecta do texto). O backend valida a sequência contígua.
 */
export function countVariables(body: string): number {
  const matches = body.match(/\{\{\s*(\d+)\s*\}\}/g);
  if (matches === null) return 0;
  const positions = new Set(matches.map((m) => m.replace(/[^\d]/g, '')));
  return positions.size;
}

/**
 * Placeholders neutros para o preview. O gabarito usava valores realistas
 * ("João Silva") — trocados por placeholders explícitos para nunca exibir
 * dado inventado como se fosse real (preview também aparece no seletor de
 * template do atendente).
 */
const PREVIEW_MOCK_VALUES = ['[nome]', '[data]', '[período]', '[valor]', '[plano]'];

/** Substitui `{{n}}` por valores de exemplo para o preview WhatsApp. */
export function renderPreviewBody(body: string): string {
  let out = body;
  for (let i = 0; i < PREVIEW_MOCK_VALUES.length; i++) {
    out = out.replace(
      new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'),
      PREVIEW_MOCK_VALUES[i] ?? '',
    );
  }
  return out;
}

/** Formata data ISO para "DD/MMM HH:mm" curto (lista). `null` → "—". */
export function formatShortDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const months = [
    'Jan',
    'Fev',
    'Mar',
    'Abr',
    'Mai',
    'Jun',
    'Jul',
    'Ago',
    'Set',
    'Out',
    'Nov',
    'Dez',
  ];
  const day = String(d.getDate()).padStart(2, '0');
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  const mon = months[d.getMonth()] ?? '';
  return `${day}/${mon} ${hh}:${mm}`;
}

/** Edição de conteúdo só é permitida em draft/rejected (backend 422 caso contrário). */
export function isContentEditable(status: string): boolean {
  return status === 'draft' || status === 'rejected';
}
